import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiLoader,
    FiSave,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedPurchaseBill,
    createPurchaseBill,
    fetchPurchaseBillById,
    purchaseBillErrorSelector,
    purchaseBillLoadingSelector,
    selectedPurchaseBillSelector,
    updatePurchaseBill,
} from "../../../../../../store/billing/purchaseBills/purchaseBillSlice";

import {
    activeSupplierListSelector,
    fetchActiveSuppliers,
    supplierErrorSelector,
    supplierLoadingSelector,
} from "../../../../../../store/billing/suppliers/supplierSlice";

/*
|--------------------------------------------------------------------------
| REUSE INVOICE ITEM TABLE
|--------------------------------------------------------------------------
|
| IMPORTANT:
|
| This assumes your SaleInvoiceFormPage.jsx is located in:
|
| BillingManagement/SaleInvoices/
|
| and InvoiceItemsTable.jsx is:
|
| BillingManagement/SaleInvoices/components/InvoiceItemsTable.jsx
|
| If your folder name is slightly different, change ONLY this import path.
|--------------------------------------------------------------------------
*/

import InvoiceItemsTable, {
    calculateInvoiceItem,
    createEmptyInvoiceItem,
    normalizeInvoiceItem,
    validateInvoiceItem,
} from "../SaleInvoices/components/InvoiceItemsTable";

import "./PurchaseBills.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const BUSINESS_STATE =
    "Rajasthan";

const BUSINESS_STATE_CODE =
    "08";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) => {
    return (
        Math.round(
            (
                toNumber(
                    value
                ) +
                Number.EPSILON
            ) *
                100
        ) / 100
    );
};

const formatCurrency = (
    value
) => {
    return new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        toNumber(
            value
        )
    );
};

const getToday = () => {
    const date =
        new Date();

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() +
                1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
};

const getDateInputValue = (
    value
) => {
    if (!value) {
        return "";
    }

    const date =
        new Date(
            value
        );

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    return date
        .toISOString()
        .slice(
            0,
            10
        );
};

const normalizeText = (
    value
) =>
    String(
        value ?? ""
    ).trim();

/*
|--------------------------------------------------------------------------
| SUPPLIER HELPERS
|--------------------------------------------------------------------------
*/

const getSupplierId = (
    supplier
) =>
    supplier?._id ||
    supplier?.id ||
    "";

const getSupplierName = (
    supplier
) =>
    supplier?.supplierName ||
    supplier?.companyName ||
    supplier?.name ||
    "";

const getSupplierMobile = (
    supplier
) =>
    supplier?.mobile ||
    "";

const getSupplierDisplayName = (
    supplier
) => {
    return [
        getSupplierName(
            supplier
        ),

        getSupplierMobile(
            supplier
        ),
    ]
        .filter(
            Boolean
        )
        .join(
            " - "
        );
};

/*
|--------------------------------------------------------------------------
| PRODUCT ITEM MAPPING
|--------------------------------------------------------------------------
|
| Our InvoiceItemsTable uses:
|
| productId
| itemName
| hsnSacCode
| quantity
| rate
| priceMode
| discountType
| discountValue
| taxPercentage
|
| Purchase_Bill backend currently expects:
|
| product
| inventory
| itemName
| hsnSac
| quantity
| rate
| discountPercent
| discountAmount
| taxRate
|--------------------------------------------------------------------------
*/

const mapPurchaseBillItemToInvoiceItem =
    (
        item
    ) => {
        const discountPercent =
            toNumber(
                item
                    ?.discountPercent
            );

        const normalized =
            normalizeInvoiceItem({
                temporaryId:
                    item
                        ?.temporaryId ||
                    item?._id,

                productId:
                    item
                        ?.product
                        ?._id ||
                    item
                        ?.product ||
                    item
                        ?.inventory
                        ?._id ||
                    item
                        ?.inventory ||
                    "",

                itemType:
                    item
                        ?.itemType ||
                    (
                        item
                            ?.product ||
                        item
                            ?.inventory
                            ? "INVENTORY"
                            : "MANUAL"
                    ),

                itemName:
                    item
                        ?.itemName ||
                    "",

                description:
                    item
                        ?.description ||
                    "",

                hsnSacCode:
                    item
                        ?.hsnSac ||
                    item
                        ?.hsnSacCode ||
                    "",

                quantity:
                    item
                        ?.quantity ??
                    1,

                unit:
                    String(
                        item
                            ?.unit ||
                            "piece"
                    ).toLowerCase(),

                rate:
                    item
                        ?.rate ??
                    0,

                priceMode:
                    item
                        ?.priceMode ||
                    "TAX_EXCLUSIVE",

                discountType:
                    item
                        ?.discountType ||
                    (
                        discountPercent >
                        0
                            ? "PERCENTAGE"
                            : "NONE"
                    ),

                discountValue:
                    item
                        ?.discountValue ??
                    discountPercent,

                taxPercentage:
                    item
                        ?.taxRate ??
                    item
                        ?.taxPercentage ??
                    0,

                cessPercentage:
                    item
                        ?.cessPercentage ??
                    0,

                metadata: {
                    ...(item
                        ?.metadata ||
                        {}),

                    inventoryId:
                        item
                            ?.inventory
                            ?._id ||
                        item
                            ?.inventory ||
                        "",

                    purchasePriceSnapshot:
                        item
                            ?.rate ??
                        0,
                },
            });

        return normalized;
    };

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const PurchaseBillFormPage =
    () => {
        const dispatch =
            useDispatch();

        const history =
            useHistory();

        const {
            id,
        } =
            useParams();

        const isEdit =
            Boolean(
                id
            );

        /*
        |--------------------------------------------------------------------------
        | REDUX - PURCHASE BILL
        |--------------------------------------------------------------------------
        */

        const purchaseBill =
            useSelector(
                selectedPurchaseBillSelector
            );

        const loading =
            useSelector(
                purchaseBillLoadingSelector
            ) || {};

        const errors =
            useSelector(
                purchaseBillErrorSelector
            ) || {};

        /*
        |--------------------------------------------------------------------------
        | REDUX - SUPPLIER
        |--------------------------------------------------------------------------
        */

        const suppliers =
            useSelector(
                activeSupplierListSelector
            ) || [];

        const supplierLoading =
            useSelector(
                supplierLoadingSelector
            ) || {};

        const supplierErrors =
            useSelector(
                supplierErrorSelector
            ) || {};

        /*
        |--------------------------------------------------------------------------
        | FORM STATE
        |--------------------------------------------------------------------------
        */

        const [
            supplierId,
            setSupplierId,
        ] =
            useState(
                ""
            );

        const [
            documentDate,
            setDocumentDate,
        ] =
            useState(
                getToday()
            );

        const [
            supplierInvoiceNumber,
            setSupplierInvoiceNumber,
        ] =
            useState(
                ""
            );

        const [
            supplierInvoiceDate,
            setSupplierInvoiceDate,
        ] =
            useState(
                ""
            );

        const [
            dueDate,
            setDueDate,
        ] =
            useState(
                ""
            );

        const [
            placeOfSupply,
            setPlaceOfSupply,
        ] =
            useState(
                BUSINESS_STATE
            );

        const [
            placeOfSupplyStateCode,
            setPlaceOfSupplyStateCode,
        ] =
            useState(
                BUSINESS_STATE_CODE
            );

        /*
        |--------------------------------------------------------------------------
        | ITEMS
        |--------------------------------------------------------------------------
        */

        const [
            items,
            setItems,
        ] =
            useState([
                createEmptyInvoiceItem({
                    defaultUnit:
                        "piece",

                    defaultPriceMode:
                        "TAX_EXCLUSIVE",

                    defaultTaxPercentage:
                        18,
                }),
            ]);

        const [
            itemValidation,
            setItemValidation,
        ] =
            useState({
                valid:
                    false,

                rowErrors:
                    [],
            });

        /*
        |--------------------------------------------------------------------------
        | OTHER DETAILS
        |--------------------------------------------------------------------------
        */

        const [
            notes,
            setNotes,
        ] =
            useState(
                ""
            );

        const [
            internalNotes,
            setInternalNotes,
        ] =
            useState(
                ""
            );

        const [
            enableRoundOff,
            setEnableRoundOff,
        ] =
            useState(
                true
            );

        const [
            localError,
            setLocalError,
        ] =
            useState(
                ""
            );

        /*
        |--------------------------------------------------------------------------
        | LOAD SUPPLIERS
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                dispatch(
                    fetchActiveSuppliers({
                        limit:
                            500,
                    })
                );
            },
            [
                dispatch,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | LOAD PURCHASE BILL
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                if (
                    !isEdit
                ) {
                    return undefined;
                }

                dispatch(
                    fetchPurchaseBillById(
                        id
                    )
                );

                return () => {
                    dispatch(
                        clearSelectedPurchaseBill()
                    );
                };
            },
            [
                dispatch,
                id,
                isEdit,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | POPULATE EDIT
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                if (
                    !isEdit ||
                    !purchaseBill
                ) {
                    return;
                }

                const status =
                    String(
                        purchaseBill
                            ?.documentStatus ||
                            purchaseBill
                                ?.status ||
                            ""
                    ).toUpperCase();

                if (
                    status !==
                    "DRAFT"
                ) {
                    setLocalError(
                        "Only draft purchase bills can be edited."
                    );
                }

                setSupplierId(
                    purchaseBill
                        ?.supplier
                        ?._id ||
                        purchaseBill
                            ?.supplier ||
                        ""
                );

                setDocumentDate(
                    getDateInputValue(
                        purchaseBill
                            ?.documentDate
                    )
                );

                setSupplierInvoiceNumber(
                    purchaseBill
                        ?.supplierInvoiceNumber ||
                        ""
                );

                setSupplierInvoiceDate(
                    getDateInputValue(
                        purchaseBill
                            ?.supplierInvoiceDate
                    )
                );

                setDueDate(
                    getDateInputValue(
                        purchaseBill
                            ?.dueDate
                    )
                );

                setPlaceOfSupply(
                    purchaseBill
                        ?.placeOfSupply ||
                        BUSINESS_STATE
                );

                /*
                |--------------------------------------------------------------------------
                | Infer state code from saved GST tax type
                |--------------------------------------------------------------------------
                */

                setPlaceOfSupplyStateCode(
                    purchaseBill
                        ?.taxType ===
                    "IGST"
                        ? ""
                        : BUSINESS_STATE_CODE
                );

                setNotes(
                    purchaseBill
                        ?.notes ||
                        ""
                );

                setInternalNotes(
                    purchaseBill
                        ?.internalNotes ||
                        ""
                );

                /*
                |--------------------------------------------------------------------------
                | ROUND OFF
                |--------------------------------------------------------------------------
                */

                setEnableRoundOff(
                    Math.abs(
                        toNumber(
                            purchaseBill
                                ?.totals
                                ?.roundOffAmount
                        )
                    ) >
                        0 ||
                        true
                );

                /*
                |--------------------------------------------------------------------------
                | ITEMS
                |--------------------------------------------------------------------------
                */

                if (
                    Array.isArray(
                        purchaseBill
                            ?.items
                    ) &&
                    purchaseBill
                        .items
                        .length >
                        0
                ) {
                    setItems(
                        purchaseBill.items.map(
                            mapPurchaseBillItemToInvoiceItem
                        )
                    );
                }
            },
            [
                isEdit,
                purchaseBill,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | SELECTED SUPPLIER
        |--------------------------------------------------------------------------
        */

        const selectedSupplier =
            useMemo(
                () =>
                    suppliers.find(
                        (
                            supplier
                        ) =>
                            String(
                                getSupplierId(
                                    supplier
                                )
                            ) ===
                            String(
                                supplierId
                            )
                    ) ||
                    null,
                [
                    suppliers,
                    supplierId,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | GST TYPE
        |--------------------------------------------------------------------------
        |
        | Business:
        | Rajasthan / 08
        |
        | Supplier supply state same:
        | CGST + SGST
        |
        | Other state:
        | IGST
        |--------------------------------------------------------------------------
        */

        const isInterState =
            Boolean(
                placeOfSupplyStateCode &&
                    BUSINESS_STATE_CODE &&
                    String(
                        placeOfSupplyStateCode
                    ).padStart(
                        2,
                        "0"
                    ) !==
                        String(
                            BUSINESS_STATE_CODE
                        ).padStart(
                            2,
                            "0"
                        )
            );

        const taxType =
            isInterState
                ? "IGST"
                : "CGST_SGST";

        /*
        |--------------------------------------------------------------------------
        | ITEMS CHANGE
        |--------------------------------------------------------------------------
        |
        | IMPORTANT:
        |
        | InvoiceItemsTable normally fills sellingPrice.
        |
        | But when selecting an inventory product for PURCHASE,
        | InvoiceItemsTable already keeps `purchasePriceSnapshot`
        | in metadata.
        |
        | So when product changes we replace rate ONLY ONCE
        | with purchase price.
        |
        | After that user can manually edit purchase rate.
        |--------------------------------------------------------------------------
        */

        const handleItemsChange =
            useCallback(
                (
                    nextItems
                ) => {
                    const normalized =
                        nextItems.map(
                            (
                                nextItem,
                                index
                            ) => {
                                const previousItem =
                                    items[
                                        index
                                    ];

                                const productChanged =
                                    nextItem
                                        ?.productId &&
                                    String(
                                        nextItem
                                            .productId
                                    ) !==
                                        String(
                                            previousItem
                                                ?.productId ||
                                                ""
                                        );

                                if (
                                    productChanged
                                ) {
                                    const purchasePrice =
                                        toNumber(
                                            nextItem
                                                ?.metadata
                                                ?.purchasePriceSnapshot,
                                            nextItem
                                                ?.rate ||
                                                0
                                        );

                                    return {
                                        ...nextItem,

                                        rate:
                                            purchasePrice,
                                    };
                                }

                                return nextItem;
                            }
                        );

                    setItems(
                        normalized
                    );
                },
                [
                    items,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | CALCULATED ITEMS
        |--------------------------------------------------------------------------
        |
        | Same calculation engine used by Invoice.
        |--------------------------------------------------------------------------
        */

        const calculatedItems =
            useMemo(
                () =>
                    items.map(
                        (
                            item
                        ) => ({
                            ...item,

                            ...calculateInvoiceItem(
                                item,
                                isInterState
                            ),
                        })
                    ),
                [
                    items,
                    isInterState,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | TOTALS
        |--------------------------------------------------------------------------
        |
        | Same order/calculation concept as Sale Invoice:
        |
        | Subtotal
        | Discount
        | Taxable
        | CGST / SGST / IGST
        | Total Tax
        | Round Off
        | Grand Total
        |--------------------------------------------------------------------------
        */

        const totals =
            useMemo(
                () => {
                    const result =
                        calculatedItems.reduce(
                            (
                                total,
                                item
                            ) => {
                                const hasItem =
                                    Boolean(
                                        normalizeText(
                                            item
                                                ?.itemName
                                        ) ||
                                            item
                                                ?.productId
                                    );

                                if (
                                    !hasItem
                                ) {
                                    return total;
                                }

                                return {
                                    itemCount:
                                        total
                                            .itemCount +
                                        1,

                                    totalQuantity:
                                        total
                                            .totalQuantity +
                                        toNumber(
                                            item
                                                ?.quantity
                                        ),

                                    subtotal:
                                        total
                                            .subtotal +
                                        toNumber(
                                            item
                                                ?.grossAmount
                                        ),

                                    discountAmount:
                                        total
                                            .discountAmount +
                                        toNumber(
                                            item
                                                ?.discountAmount
                                        ),

                                    taxableAmount:
                                        total
                                            .taxableAmount +
                                        toNumber(
                                            item
                                                ?.taxableAmount
                                        ),

                                    cgstAmount:
                                        total
                                            .cgstAmount +
                                        toNumber(
                                            item
                                                ?.cgstAmount
                                        ),

                                    sgstAmount:
                                        total
                                            .sgstAmount +
                                        toNumber(
                                            item
                                                ?.sgstAmount
                                        ),

                                    igstAmount:
                                        total
                                            .igstAmount +
                                        toNumber(
                                            item
                                                ?.igstAmount
                                        ),

                                    taxAmount:
                                        total
                                            .taxAmount +
                                        toNumber(
                                            item
                                                ?.taxAmount
                                        ),

                                    cessAmount:
                                        total
                                            .cessAmount +
                                        toNumber(
                                            item
                                                ?.cessAmount
                                        ),

                                    totalBeforeRoundOff:
                                        total
                                            .totalBeforeRoundOff +
                                        toNumber(
                                            item
                                                ?.totalAmount
                                        ),
                                };
                            },
                            {
                                itemCount:
                                    0,

                                totalQuantity:
                                    0,

                                subtotal:
                                    0,

                                discountAmount:
                                    0,

                                taxableAmount:
                                    0,

                                cgstAmount:
                                    0,

                                sgstAmount:
                                    0,

                                igstAmount:
                                    0,

                                taxAmount:
                                    0,

                                cessAmount:
                                    0,

                                totalBeforeRoundOff:
                                    0,
                            }
                        );

                    const subtotal =
                        roundMoney(
                            result
                                .subtotal
                        );

                    const discountAmount =
                        roundMoney(
                            result
                                .discountAmount
                        );

                    const taxableAmount =
                        roundMoney(
                            result
                                .taxableAmount
                        );

                    const cgstAmount =
                        roundMoney(
                            result
                                .cgstAmount
                        );

                    const sgstAmount =
                        roundMoney(
                            result
                                .sgstAmount
                        );

                    const igstAmount =
                        roundMoney(
                            result
                                .igstAmount
                        );

                    const taxAmount =
                        roundMoney(
                            result
                                .taxAmount
                        );

                    const cessAmount =
                        roundMoney(
                            result
                                .cessAmount
                        );

                    const totalBeforeRoundOff =
                        roundMoney(
                            result
                                .totalBeforeRoundOff
                        );

                    /*
                    |--------------------------------------------------------------------------
                    | SAME ROUND-OFF STYLE AS INVOICE
                    |--------------------------------------------------------------------------
                    */

                    const roundedTotal =
                        Math.round(
                            totalBeforeRoundOff
                        );

                    const roundOffAmount =
                        enableRoundOff
                            ? roundMoney(
                                  roundedTotal -
                                      totalBeforeRoundOff
                              )
                            : 0;

                    return {
                        ...result,

                        subtotal,

                        discountAmount,

                        taxableAmount,

                        cgstAmount,

                        sgstAmount,

                        igstAmount,

                        taxAmount,

                        cessAmount,

                        totalBeforeRoundOff,

                        roundOffAmount,

                        grandTotal:
                            roundMoney(
                                totalBeforeRoundOff +
                                    roundOffAmount
                            ),
                    };
                },
                [
                    calculatedItems,
                    enableRoundOff,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | VALIDATION
        |--------------------------------------------------------------------------
        */

        const validate =
            () => {
                if (
                    !supplierId
                ) {
                    return "Please select a supplier.";
                }

                if (
                    !documentDate
                ) {
                    return "Purchase bill date is required.";
                }

                const validItems =
                    items.filter(
                        (
                            item
                        ) =>
                            normalizeText(
                                item
                                    ?.itemName
                            ) ||
                            item
                                ?.productId
                    );

                if (
                    !validItems.length
                ) {
                    return "Add at least one purchase item.";
                }

                /*
                |--------------------------------------------------------------------------
                | For PURCHASE:
                |
                | allow zero current stock
                | allow quantity > current stock
                |
                | because we're ADDING stock.
                |--------------------------------------------------------------------------
                */

                for (
                    let index = 0;
                    index <
                    validItems.length;
                    index += 1
                ) {
                    const item =
                        validItems[
                            index
                        ];

                    const validationErrors =
                        validateInvoiceItem(
                            item,
                            {
                                allowZeroStock:
                                    true,

                                allowOverSelling:
                                    true,
                            }
                        );

                    if (
                        validationErrors
                            ?.itemName
                    ) {
                        return `Row ${
                            index +
                            1
                        }: ${validationErrors.itemName}`;
                    }

                    if (
                        validationErrors
                            ?.quantity
                    ) {
                        return `Row ${
                            index +
                            1
                        }: ${validationErrors.quantity}`;
                    }

                    if (
                        validationErrors
                            ?.rate
                    ) {
                        return `Row ${
                            index +
                            1
                        }: ${validationErrors.rate}`;
                    }

                    if (
                        validationErrors
                            ?.discountValue
                    ) {
                        return `Row ${
                            index +
                            1
                        }: ${validationErrors.discountValue}`;
                    }
                }

                if (
                    totals
                        .grandTotal <
                    0
                ) {
                    return "Grand total cannot be negative.";
                }

                return "";
            };

        /*
        |--------------------------------------------------------------------------
        | BACKEND ITEMS
        |--------------------------------------------------------------------------
        */

        const buildBackendItems =
            () => {
                return items
                    .filter(
                        (
                            item
                        ) =>
                            normalizeText(
                                item
                                    ?.itemName
                            ) ||
                            item
                                ?.productId
                    )
                    .map(
                        (
                            item
                        ) => {
                            const calculation =
                                calculateInvoiceItem(
                                    item,
                                    isInterState
                                );

                            return {
                                /*
                                |--------------------------------------------------------------------------
                                | PRODUCT REFERENCES
                                |--------------------------------------------------------------------------
                                */

                                product:
                                    item
                                        ?.productId ||
                                    null,

                                inventory:
                                    item
                                        ?.productId ||
                                    null,

                                itemType:
                                    item
                                        ?.itemType ||
                                    (
                                        item
                                            ?.productId
                                            ? "INVENTORY"
                                            : "MANUAL"
                                    ),

                                /*
                                |--------------------------------------------------------------------------
                                | DESCRIPTION
                                |--------------------------------------------------------------------------
                                */

                                itemName:
                                    normalizeText(
                                        item
                                            ?.itemName
                                    ),

                                description:
                                    normalizeText(
                                        item
                                            ?.description
                                    ),

                                hsnSac:
                                    normalizeText(
                                        item
                                            ?.hsnSacCode
                                    ),

                                unit:
                                    String(
                                        item
                                            ?.unit ||
                                            "piece"
                                    ).toUpperCase(),

                                /*
                                |--------------------------------------------------------------------------
                                | QTY / PRICE
                                |--------------------------------------------------------------------------
                                */

                                quantity:
                                    toNumber(
                                        item
                                            ?.quantity
                                    ),

                                rate:
                                    roundMoney(
                                        item
                                            ?.rate
                                    ),

                                priceMode:
                                    item
                                        ?.priceMode ||
                                    "TAX_EXCLUSIVE",

                                /*
                                |--------------------------------------------------------------------------
                                | DISCOUNT
                                |--------------------------------------------------------------------------
                                */

                                discountType:
                                    item
                                        ?.discountType ||
                                    "NONE",

                                discountValue:
                                    roundMoney(
                                        item
                                            ?.discountValue
                                    ),

                                discountPercent:
                                    item
                                        ?.discountType ===
                                    "PERCENTAGE"
                                        ? roundMoney(
                                              item
                                                  ?.discountValue
                                          )
                                        : 0,

                                discountAmount:
                                    calculation
                                        .discountAmount,

                                /*
                                |--------------------------------------------------------------------------
                                | GST
                                |--------------------------------------------------------------------------
                                */

                                taxRate:
                                    roundMoney(
                                        item
                                            ?.taxPercentage
                                    ),

                                taxPercentage:
                                    roundMoney(
                                        item
                                            ?.taxPercentage
                                    ),

                                cessPercentage:
                                    roundMoney(
                                        item
                                            ?.cessPercentage
                                    ),

                                /*
                                |--------------------------------------------------------------------------
                                | CALCULATED VALUES
                                |--------------------------------------------------------------------------
                                */

                                grossAmount:
                                    calculation
                                        .grossAmount,

                                taxableAmount:
                                    calculation
                                        .taxableAmount,

                                cgstAmount:
                                    calculation
                                        .cgstAmount,

                                sgstAmount:
                                    calculation
                                        .sgstAmount,

                                igstAmount:
                                    calculation
                                        .igstAmount,

                                taxAmount:
                                    calculation
                                        .taxAmount,

                                cessAmount:
                                    calculation
                                        .cessAmount,

                                totalAmount:
                                    calculation
                                        .totalAmount,

                                metadata: {
                                    ...(item
                                        ?.metadata ||
                                        {}),

                                    sku:
                                        item
                                            ?.sku ||
                                        "",

                                    barcode:
                                        item
                                            ?.barcode ||
                                        "",

                                    brand:
                                        item
                                            ?.brand ||
                                        "",

                                    modelNumber:
                                        item
                                            ?.modelNumber ||
                                        "",
                                },
                            };
                        }
                    );
            };

        /*
        |--------------------------------------------------------------------------
        | PAYLOAD
        |--------------------------------------------------------------------------
        */

        const buildPayload =
            () => {
                return {
                    supplierId,

                    documentDate,

                    supplierInvoiceNumber:
                        supplierInvoiceNumber.trim(),

                    supplierInvoiceDate:
                        supplierInvoiceDate ||
                        null,

                    dueDate:
                        dueDate ||
                        null,

                    /*
                    |--------------------------------------------------------------------------
                    | TAX
                    |--------------------------------------------------------------------------
                    */

                    taxType,

                    placeOfSupply:
                        placeOfSupply.trim(),

                    placeOfSupplyStateCode,

                    /*
                    |--------------------------------------------------------------------------
                    | TOTALS
                    |--------------------------------------------------------------------------
                    */

                    roundOffAmount:
                        totals
                            .roundOffAmount,

                    enableRoundOff,

                    /*
                    |--------------------------------------------------------------------------
                    | NOTES
                    |--------------------------------------------------------------------------
                    */

                    notes:
                        notes.trim(),

                    internalNotes:
                        internalNotes.trim(),

                    /*
                    |--------------------------------------------------------------------------
                    | ITEMS
                    |--------------------------------------------------------------------------
                    */

                    items:
                        buildBackendItems(),

                    /*
                    |--------------------------------------------------------------------------
                    | Frontend total snapshot
                    |--------------------------------------------------------------------------
                    |
                    | Backend should still recalculate all amounts.
                    |--------------------------------------------------------------------------
                    */

                    totals: {
                        itemCount:
                            totals
                                .itemCount,

                        totalQuantity:
                            roundMoney(
                                totals
                                    .totalQuantity
                            ),

                        grossAmount:
                            totals
                                .subtotal,

                        subtotal:
                            totals
                                .subtotal,

                        discountAmount:
                            totals
                                .discountAmount,

                        taxableAmount:
                            totals
                                .taxableAmount,

                        cgstAmount:
                            totals
                                .cgstAmount,

                        sgstAmount:
                            totals
                                .sgstAmount,

                        igstAmount:
                            totals
                                .igstAmount,

                        taxAmount:
                            totals
                                .taxAmount,

                        cessAmount:
                            totals
                                .cessAmount,

                        roundOffAmount:
                            totals
                                .roundOffAmount,

                        grandTotal:
                            totals
                                .grandTotal,
                    },
                };
            };

        /*
        |--------------------------------------------------------------------------
        | SAVE
        |--------------------------------------------------------------------------
        */

        const handleSave =
            async () => {
                const validationError =
                    validate();

                if (
                    validationError
                ) {
                    setLocalError(
                        validationError
                    );

                    return;
                }

                try {
                    setLocalError(
                        ""
                    );

                    let result =
                        null;

                    if (
                        isEdit
                    ) {
                        result =
                            await dispatch(
                                updatePurchaseBill({
                                    purchaseBillId:
                                        id,

                                    payload:
                                        buildPayload(),
                                })
                            ).unwrap();
                    } else {
                        result =
                            await dispatch(
                                createPurchaseBill(
                                    buildPayload()
                                )
                            ).unwrap();
                    }

                    const savedId =
                        result
                            ?._id ||
                        result
                            ?.data
                            ?._id ||
                        result
                            ?.purchaseBill
                            ?._id;

                    if (
                        savedId
                    ) {
                        history.push(
                            `/billing/purchase-bills/${savedId}`
                        );

                        return;
                    }

                    history.push(
                        "/billing/purchase-bills"
                    );
                } catch (
                    error
                ) {
                    setLocalError(
                        typeof error ===
                            "string"
                            ? error
                            : error
                                  ?.message ||
                              "Unable to save purchase bill."
                    );
                }
            };

        /*
        |--------------------------------------------------------------------------
        | CANCEL
        |--------------------------------------------------------------------------
        */

        const handleCancel =
            () => {
                if (
                    isEdit &&
                    id
                ) {
                    history.push(
                        `/billing/purchase-bills/${id}`
                    );

                    return;
                }

                history.push(
                    "/billing/purchase-bills"
                );
            };

        const saving =
            Boolean(
                loading
                    ?.create ||
                    loading
                        ?.update
            );

        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="purchase-bill-page">
                {/* ==========================================================
                    HEADER
                   ========================================================== */}

                <header className="purchase-bill-header">
                    <div className="purchase-bill-heading">
                        <button
                            type="button"
                            className="purchase-bill-icon-button"
                            onClick={
                                handleCancel
                            }
                        >
                            <FiArrowLeft />
                        </button>

                        <div>
                            <small>
                                PURCHASE BILL
                            </small>

                            <h1>
                                {isEdit
                                    ? "Edit Purchase Bill"
                                    : "New Purchase Bill"}
                            </h1>

                            <p>
                                Record supplier purchase, tax and inventory receipt.
                            </p>
                        </div>
                    </div>

                    <div className="purchase-bill-header-actions">
                        <button
                            type="button"
                            className="purchase-bill-button"
                            disabled={
                                saving
                            }
                            onClick={
                                handleCancel
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="purchase-bill-button purchase-bill-button-primary"
                            disabled={
                                saving
                            }
                            onClick={
                                handleSave
                            }
                        >
                            {saving ? (
                                <FiLoader className="purchase-bill-spinner" />
                            ) : (
                                <FiSave />
                            )}

                            {isEdit
                                ? "Update Draft"
                                : "Save Draft"}
                        </button>
                    </div>
                </header>

                {/* ==========================================================
                    ERROR
                   ========================================================== */}

                {(localError ||
                    errors
                        ?.create ||
                    errors
                        ?.update ||
                    errors
                        ?.details ||
                    supplierErrors
                        ?.active) && (
                    <div className="purchase-bill-alert">
                        {localError ||
                            errors
                                ?.create ||
                            errors
                                ?.update ||
                            errors
                                ?.details ||
                            supplierErrors
                                ?.active}
                    </div>
                )}

                {isEdit &&
                loading
                    ?.details &&
                !purchaseBill ? (
                    <div className="purchase-bill-empty">
                        <FiLoader className="purchase-bill-spinner" />

                        <h3>
                            Loading purchase bill...
                        </h3>
                    </div>
                ) : (
                    <>
                        {/* ==================================================
                            BILL DETAILS
                           ================================================== */}

                        <section className="purchase-bill-form-card">
                            <div className="purchase-bill-section-title">
                                <h3>
                                    Bill Details
                                </h3>

                                <p>
                                    Select supplier and enter supplier invoice details.
                                </p>
                            </div>

                            <div className="purchase-bill-form-grid">
                                {/* SUPPLIER */}

                                <div className="purchase-bill-form-field purchase-bill-form-field-wide">
                                    <label>
                                        Supplier *
                                    </label>

                                    <select
                                        value={
                                            supplierId
                                        }
                                        disabled={
                                            supplierLoading
                                                ?.active
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setSupplierId(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    >
                                        <option value="">
                                            {supplierLoading
                                                ?.active
                                                ? "Loading suppliers..."
                                                : "Select Supplier"}
                                        </option>

                                        {suppliers.map(
                                            (
                                                supplier
                                            ) => (
                                                <option
                                                    key={
                                                        getSupplierId(
                                                            supplier
                                                        )
                                                    }
                                                    value={
                                                        getSupplierId(
                                                            supplier
                                                        )
                                                    }
                                                >
                                                    {getSupplierDisplayName(
                                                        supplier
                                                    )}
                                                </option>
                                            )
                                        )}
                                    </select>
                                </div>

                                {/* BILL DATE */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        Bill Date *
                                    </label>

                                    <input
                                        type="date"
                                        value={
                                            documentDate
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setDocumentDate(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                {/* SUPPLIER INVOICE */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        Supplier Invoice No.
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            supplierInvoiceNumber
                                        }
                                        placeholder="Supplier invoice number"
                                        onChange={(
                                            event
                                        ) =>
                                            setSupplierInvoiceNumber(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                {/* SUPPLIER INVOICE DATE */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        Supplier Invoice Date
                                    </label>

                                    <input
                                        type="date"
                                        value={
                                            supplierInvoiceDate
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setSupplierInvoiceDate(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                {/* DUE DATE */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        Due Date
                                    </label>

                                    <input
                                        type="date"
                                        value={
                                            dueDate
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setDueDate(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                {/* TAX TYPE */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        Tax Type
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            isInterState
                                                ? "IGST"
                                                : "CGST + SGST"
                                        }
                                        readOnly
                                    />
                                </div>

                                {/* PLACE OF SUPPLY */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        Place of Supply
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            placeOfSupply
                                        }
                                        placeholder="Rajasthan"
                                        onChange={(
                                            event
                                        ) =>
                                            setPlaceOfSupply(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                {/* STATE CODE */}

                                <div className="purchase-bill-form-field">
                                    <label>
                                        State Code
                                    </label>

                                    <input
                                        type="text"
                                        maxLength="2"
                                        value={
                                            placeOfSupplyStateCode
                                        }
                                        placeholder="08"
                                        onChange={(
                                            event
                                        ) =>
                                            setPlaceOfSupplyStateCode(
                                                event
                                                    .target
                                                    .value
                                                    .replace(
                                                        /\D/g,
                                                        ""
                                                    )
                                                    .slice(
                                                        0,
                                                        2
                                                    )
                                            )
                                        }
                                    />
                                </div>
                            </div>

                            {/* SELECTED SUPPLIER */}

                            {supplierId && (
                                <div className="purchase-bill-selected-supplier">
                                    <strong>
                                        {selectedSupplier
                                            ? getSupplierName(
                                                  selectedSupplier
                                              )
                                            : purchaseBill
                                                  ?.supplierSnapshot
                                                  ?.supplierName ||
                                              "Supplier"}
                                    </strong>

                                    <span>
                                        {selectedSupplier
                                            ? getSupplierMobile(
                                                  selectedSupplier
                                              )
                                            : purchaseBill
                                                  ?.supplierSnapshot
                                                  ?.mobile ||
                                              ""}
                                    </span>

                                    {(selectedSupplier
                                        ?.gstin ||
                                        purchaseBill
                                            ?.supplierSnapshot
                                            ?.gstin) && (
                                        <span>
                                            GSTIN:{" "}
                                            {selectedSupplier
                                                ?.gstin ||
                                                purchaseBill
                                                    ?.supplierSnapshot
                                                    ?.gstin}
                                        </span>
                                    )}
                                </div>
                            )}
                        </section>

                        {/* ==================================================
                            PRODUCTS / ITEMS
                           ==================================================
                           
                           EXACT SAME COMPONENT USED BY SALE INVOICE
                           ================================================== */}

                        <InvoiceItemsTable
                            items={
                                items
                            }
                            onChange={
                                handleItemsChange
                            }
                            onValidationChange={
                                setItemValidation
                            }
                            defaults={{
                                defaultUnit:
                                    "piece",

                                defaultPriceMode:
                                    "TAX_EXCLUSIVE",

                                defaultTaxPercentage:
                                    18,
                            }}
                            disabled={
                                saving
                            }
                            allowManual={
                                true
                            }

                            /*
                            |--------------------------------------------------------------------------
                            | PURCHASE BILL IMPORTANT
                            |--------------------------------------------------------------------------
                            |
                            | Current stock must NOT restrict purchasing.
                            |--------------------------------------------------------------------------
                            */

                            allowZeroStock={
                                true
                            }
                            allowOverSelling={
                                true
                            }

                            isInterState={
                                isInterState
                            }
                            minimumRows={
                                1
                            }
                            showDescription={
                                true
                            }
                            showCess={
                                false
                            }
                            showPriceMode={
                                true
                            }
                            showStock={
                                false
                            }
                        />

                        {/* ==================================================
                            NOTES + PURCHASE BILL SUMMARY
                           ================================================== */}

                        <div className="purchase-bill-invoice-bottom-grid">
                            {/* NOTES */}

                            <section className="purchase-bill-form-card purchase-bill-notes-card">
                                <div className="purchase-bill-section-title">
                                    <h3>
                                        Notes & Details
                                    </h3>

                                    <p>
                                        Add supplier bill and internal information.
                                    </p>
                                </div>

                                <div className="purchase-bill-notes-area">
                                    <label>
                                        Notes
                                    </label>

                                    <textarea
                                        rows="4"
                                        value={
                                            notes
                                        }
                                        placeholder="Supplier bill notes..."
                                        onChange={(
                                            event
                                        ) =>
                                            setNotes(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />

                                    <label>
                                        Internal Notes
                                    </label>

                                    <textarea
                                        rows="3"
                                        value={
                                            internalNotes
                                        }
                                        placeholder="Internal notes, not visible outside"
                                        onChange={(
                                            event
                                        ) =>
                                            setInternalNotes(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>
                            </section>

                            {/* ==================================================
                                SUMMARY
                               ================================================== */}

                            <section className="purchase-bill-form-card purchase-bill-invoice-summary-card">
                                <div className="purchase-bill-summary-title">
                                    <div>
                                        <h3>
                                            Purchase Bill Summary
                                        </h3>

                                        <span>
                                            {
                                                totals
                                                    .itemCount
                                            }{" "}
                                            item
                                            {totals
                                                .itemCount !==
                                            1
                                                ? "s"
                                                : ""}
                                        </span>
                                    </div>
                                </div>

                                {/* SUBTOTAL */}

                                <div className="purchase-bill-invoice-total-row">
                                    <span>
                                        Subtotal
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            totals
                                                .subtotal
                                        )}
                                    </strong>
                                </div>

                                {/* DISCOUNT */}

                                <div className="purchase-bill-invoice-total-row">
                                    <span>
                                        Discount
                                    </span>

                                    <strong
                                        className={
                                            totals
                                                .discountAmount >
                                            0
                                                ? "purchase-bill-summary-negative"
                                                : ""
                                        }
                                    >
                                        {totals
                                            .discountAmount >
                                        0
                                            ? "-"
                                            : ""}

                                        {formatCurrency(
                                            totals
                                                .discountAmount
                                        )}
                                    </strong>
                                </div>

                                {/* TAXABLE */}

                                <div className="purchase-bill-invoice-total-row">
                                    <span>
                                        Taxable Amount
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            totals
                                                .taxableAmount
                                        )}
                                    </strong>
                                </div>

                                {/* CGST / SGST */}

                                {!isInterState && (
                                    <>
                                        <div className="purchase-bill-invoice-total-row">
                                            <span>
                                                CGST
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    totals
                                                        .cgstAmount
                                                )}
                                            </strong>
                                        </div>

                                        <div className="purchase-bill-invoice-total-row">
                                            <span>
                                                SGST
                                            </span>

                                            <strong>
                                                {formatCurrency(
                                                    totals
                                                        .sgstAmount
                                                )}
                                            </strong>
                                        </div>
                                    </>
                                )}

                                {/* IGST */}

                                {isInterState && (
                                    <div className="purchase-bill-invoice-total-row">
                                        <span>
                                            IGST
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                totals
                                                    .igstAmount
                                            )}
                                        </strong>
                                    </div>
                                )}

                                {/* CESS */}

                                {totals
                                    .cessAmount >
                                    0 && (
                                    <div className="purchase-bill-invoice-total-row">
                                        <span>
                                            Cess
                                        </span>

                                        <strong>
                                            {formatCurrency(
                                                totals
                                                    .cessAmount
                                            )}
                                        </strong>
                                    </div>
                                )}

                                {/* TAX */}

                                <div className="purchase-bill-invoice-total-row">
                                    <span>
                                        Total Tax
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            totals
                                                .taxAmount +
                                                totals
                                                    .cessAmount
                                        )}
                                    </strong>
                                </div>

                                {/* ROUND OFF */}

                                <div className="purchase-bill-invoice-total-row purchase-bill-round-off-row">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={
                                                enableRoundOff
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setEnableRoundOff(
                                                    event
                                                        .target
                                                        .checked
                                                )
                                            }
                                        />

                                        Round Off
                                    </label>

                                    <strong>
                                        {formatCurrency(
                                            totals
                                                .roundOffAmount
                                        )}
                                    </strong>
                                </div>

                                {/* GRAND TOTAL */}

                                <div className="purchase-bill-invoice-total-row purchase-bill-invoice-grand-total">
                                    <span>
                                        Grand Total
                                    </span>

                                    <strong>
                                        {formatCurrency(
                                            totals
                                                .grandTotal
                                        )}
                                    </strong>
                                </div>
                            </section>
                        </div>

                        {/* ==================================================
                            BOTTOM SAVE BAR
                           ================================================== */}

                        <div className="purchase-bill-bottom-actions">
                            <div className="purchase-bill-bottom-total">
                                <span>
                                    {
                                        totals
                                            .itemCount
                                    }{" "}
                                    item
                                    {totals
                                        .itemCount !==
                                    1
                                        ? "s"
                                        : ""}
                                </span>

                                <strong>
                                    {formatCurrency(
                                        totals
                                            .grandTotal
                                    )}
                                </strong>
                            </div>

                            <div>
                                <button
                                    type="button"
                                    className="purchase-bill-button"
                                    disabled={
                                        saving
                                    }
                                    onClick={
                                        handleCancel
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="purchase-bill-button purchase-bill-button-primary"
                                    disabled={
                                        saving
                                    }
                                    onClick={
                                        handleSave
                                    }
                                >
                                    {saving ? (
                                        <FiLoader className="purchase-bill-spinner" />
                                    ) : (
                                        <FiSave />
                                    )}

                                    {isEdit
                                        ? "Update Draft"
                                        : "Save Draft"}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    };

export default PurchaseBillFormPage;